import AddSharpIcon from '@mui/icons-material/AddSharp'
import { useSelector } from '@xstate/react'
import { useStore } from 'effector-react'
import { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { DriverMachineContext } from '~/entities/common/Driver'
import {
  getCanRentalContractMachine,
  rentalContractModel,
} from '~/entities/common/RentalContract'
import { usePermissions } from '~/entities/common/viewer'
import { Alert } from '~/shared/ui/Alerts'
import { Button } from '~/shared/ui/Buttons'
import { useModal } from '~/shared/ui/Modal'
import { CanceledModal } from '~/widgets/common/Driver/RentalContractControls/CanceledModal'
import { TerminateModal } from '../TerminateModal'
import {
  $activeRentalContract,
  $getRentalContractActiveIsLoading,
} from './model'
import { WarningCannotCreateModal } from './WarningCannotCreateModal'

export const RentalContractCreated = () => {
  const { t } = useTranslation()

  const { canRentalContractsCreate } = usePermissions()
  const {
    isModalShow: isRentalContractTerminateModalShow,
    openModal: openRentalContractTerminateModal,
    closeModal: closeRentalContractTerminateModal,
  } = useModal()
  const {
    isModalShow: isRentalContractCanceledModalShow,
    openModal: openRentalContractCanceledModal,
    closeModal: closeRentalContractCanceledModal,
  } = useModal()
  const {
    isModalShow: isModalWarningShow,
    openModal: openModalWarning,
    closeModal: closeModalWarning,
  } = useModal()

  const machineCtx = useContext(DriverMachineContext)
  const can = useSelector(machineCtx.interpreter, (state) => ({
    create_rental_contract: state.can('create_rental_contract'),
  }))

  const getRentalContractActiveIsLoading = useStore(
    $getRentalContractActiveIsLoading,
  )
  const activeRentalContract = useStore($activeRentalContract)

  const handleRentalContractCreate = useCallback(async () => {
    if (!can.create_rental_contract) {
      openModalWarning()
      return
    }
    const rentalContractCanStatus = getCanRentalContractMachine(
      activeRentalContract?.getStatus(),
    )

    if (!activeRentalContract) {
      rentalContractModel.goToRentalContractNew()
      return
    }
    if (rentalContractCanStatus?.terminate) {
      openRentalContractTerminateModal()
      return
    }
    if (rentalContractCanStatus?.canceled) {
      openRentalContractCanceledModal()
      return
    }
  }, [
    activeRentalContract,
    can.create_rental_contract,
    openModalWarning,
    openRentalContractCanceledModal,
    openRentalContractTerminateModal,
  ])

  const handleRentalContractTerminate = useCallback(
    async (reasons: string[]) => {
      await rentalContractModel.terminateRentalContractFx({
        rentalContractId: activeRentalContract?.getApiId() as UniqueId,
        reasons,
      })
      rentalContractModel.goToRentalContractNew()
    },
    [activeRentalContract],
  )
  const handleRentalContractCanceled = useCallback(async () => {
    await rentalContractModel.canceledRentalContractFx(
      activeRentalContract?.getApiId() as UniqueId,
    )
    rentalContractModel.goToRentalContractNew()
  }, [activeRentalContract])

  return (
    <>
      <Button
        show={canRentalContractsCreate}
        onClick={handleRentalContractCreate}
        startDecorator={<AddSharpIcon />}
        variant='gray'
        loading={getRentalContractActiveIsLoading}
      >
        {t('button.create a new contract')}
      </Button>

      <TerminateModal
        isShow={isRentalContractTerminateModalShow}
        onOk={handleRentalContractTerminate}
        extraBlock={
          <Alert variant='warning' sx={{ marginBottom: '24px' }}>
            {t(
              'alert.when creating a new contract the current contract will be terminated',
            )}
          </Alert>
        }
        onCancel={closeRentalContractTerminateModal}
        buttonSuccessTitle={t('button.terminate and create a new contract')}
      />
      <CanceledModal
        isShow={isRentalContractCanceledModalShow}
        onOk={handleRentalContractCanceled}
        extraBlock={
          <Alert variant='warning' sx={{ margin: '44px 0 24px 0' }}>
            {t(
              'alert.when creating a new contract the existing contract will be annulled',
            )}
          </Alert>
        }
        onCancel={closeRentalContractCanceledModal}
        buttonSuccessTitle={t('button.annul and create a new contract')}
      />
      <WarningCannotCreateModal
        isShow={isModalWarningShow}
        onCancel={closeModalWarning}
      />
    </>
  )
}
